// helpful SO links
// https://stackoverflow.com/questions/49390912/why-would-stripe-paymentrequest-canmakepayment-fail-on-localhost
// https://stackoverflow.com/questions/60865022/stripe-payment-request-button-with-subscriptions
import React, { ReactElement, useEffect, useRef } from 'react'
import { Box, Divider, Flex, Text } from 'theme-ui'
import { Button, useAlertPresenter } from 'ui'
import {
  defaultErrorMessage,
  TransactionStatus,
  updateProductSubscription,
  updateTransaction,
} from 'store/billing'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { Link, navigate } from 'gatsby'
import { RenderProcessing } from './PaymentForm'
import {
  TrackingEventAction,
  TrackingEventCategory,
  TrackingEventName,
  useTrackingHelper,
} from 'utils/useTrackingHelper'
import { format } from 'date-fns'
import sortBy from 'lodash/sortBy'
import { PlansSteps } from 'components/Plans/usePlanWizardSteps'
import getPriceDetailsForCurrency from 'utils/getPriceDetailsForCurrency'
import getFormattedPrice from 'utils/getFormattedPrice'
import { useStripe } from '@stripe/react-stripe-js'
import useCreditBalance from 'utils/useCreditBalance'
import ErrorIcon from 'images/erroricon.svg'
import { useGetBillingPaymentsQuery } from 'store/api/billing/payments'
import { CommonPriceInterval, Payment, ProductType } from 'store/api/billing/payments.interface'

const alertId = 'change-payment'

const ChangePlanSummary = (): ReactElement => {
  const dispatch = useAppDispatch()
  const {
    data: products,
    priceInterval,
    promoMode,
    selectedProductPk,
    currentProduct,
    selectedCurrency,
  } = useAppSelector(s => s.products)

  const selectedPricePoint = products
    ?.find(p => p.PK === selectedProductPk)
    ?.price_points.find(pp => pp.duration === priceInterval)
  const selectedPrice =
    selectedPricePoint && getPriceDetailsForCurrency(selectedPricePoint, selectedCurrency?.code)
  const stripe = useStripe()
  const sessionToken = useAppSelector(s => s.persistData.sessionToken)
  const { data: billingPaymentsData } = useGetBillingPaymentsQuery('', { skip: !sessionToken })
  const lastPayment = sortBy(billingPaymentsData?.payments ?? [], ['ts']).reverse()[0] as Payment
  const billingError = useAppSelector(s => s.billingPayments.error)
  const selectedProduct = products?.find(p => p.PK === selectedProductPk)
  const { transactionStatus, errorMessage, paymentUrl } = useAppSelector(s => s.payments)

  /** 
  To check whether the payment succeeded or not, we poll the getBillingProducts thunk which returns the currentProduct. 
  When the currentProduct price stripe id matches the selected product price stripe id, we declare the payment successful.
  But before this happens, the currentProduct gets updated to the new product and the current product name changes on screen.
  To prevent this change we store the currentProduct in a ref and use it on this screen.
  */
  const currentProductRef = useRef(currentProduct)
  const priceDuration = currentProductRef.current?.price?.duration || 0
  const isOtherThanMonthlyOrYearlyDuration =
    priceDuration > CommonPriceInterval.MONTHLY && priceDuration !== CommonPriceInterval.YEARLY
  const isButtonDisabled =
    currentProductRef.current?.name === selectedProduct?.name &&
    (currentProductRef.current?.subscription?.currency_amount ??
      currentProductRef.current?.subscription?.amount) === selectedPrice?.price
  const currentPrice =
    currentProductRef.current?.price &&
    getPriceDetailsForCurrency(currentProductRef.current.price, selectedCurrency?.code)
  useEffect(() => {
    if (billingError?.message) {
      dispatch(
        updateTransaction({
          transactionStatus: TransactionStatus.FAILURE,
          errorMessage: billingError.message,
        }),
      )
    }
  }, [billingError, dispatch])

  useEffect(() => {
    if (transactionStatus === TransactionStatus.SUCCESS) {
      navigate('/change-plan-success')
    } else if (paymentUrl) {
      // from crypto payment
      navigate(paymentUrl)
    }
  }, [paymentUrl, transactionStatus])

  const { creditBalance, proRated } = useCreditBalance()
  let total = 0
  // selectedPrice will always be in the correct currency. currentProduct.price.price will always be USD.
  // so we use the subscription to get the price paid in the correct currency
  if (
    currentPrice &&
    currentProductRef.current?.price &&
    lastPayment &&
    currentPrice.stripe_id !== selectedPrice?.stripe_id
  ) {
    total = (selectedPrice?.price ?? 0) - proRated + lastPayment.balance
  }

  const { trackEvent } = useTrackingHelper()

  const sendMatomoProductPlanEvent = (): void => {
    trackEvent({
      category: TrackingEventCategory.Payments,
      action:
        priceInterval === CommonPriceInterval.TRIAL
          ? TrackingEventAction.Trial
          : selectedProduct?.type === ProductType.FULL
          ? TrackingEventAction.FullControl
          : TrackingEventAction.SomeControl,
      name:
        priceInterval === CommonPriceInterval.TRIAL
          ? undefined
          : priceInterval === CommonPriceInterval.MONTHLY
          ? TrackingEventName.Month
          : TrackingEventName.Year,
    })

    if (!!promoMode) {
      trackEvent({
        category: TrackingEventCategory.Payments,
        action: TrackingEventAction.Method,
        name: TrackingEventName.Code,
      })
    }
  }

  async function handleSave(): Promise<void> {
    sendMatomoProductPlanEvent()
    if (lastPayment.method === 'crypto') {
      /* The following code would charge the customer the full price of the plan 
      *  instead of the difference in prices. we need a new api to handle this.
      dispatch(startTransaction())
     
      await dispatch(
        submitCryptoPayment({
          price_point_id: selectedPrice?.PK ?? '',
          ...(promoMode && promoCode
            ? {
                [promoMode]: promoCode,
              }
            : {}),
        }),
      )
   
      */
      dispatch(
        updateTransaction({
          transactionStatus: TransactionStatus.FAILURE,
          errorMessage: defaultErrorMessage,
        }),
      )
    } else {
      if (stripe) {
        await dispatch(updateProductSubscription({ stripe }))
      }
    }
  }

  const { presentAlert, dismissAlert } = useAlertPresenter()

  return (
    <Flex
      sx={{
        width: ['100%', '46.8rem'],
        flexDirection: 'column',
        mb: ['10rem', '2.4rem'],
        mt: '2.4rem',
      }}
    >
      <Flex
        sx={{
          width: '100%',
          p: '2.4rem',
          flexDirection: 'column',
          backgroundColor: 'smokyBlack80',
          borderRadius: '1.2rem',
          border: '1px solid',
          borderColor: 'blueYonder50',
          textAlign: 'left',
          height: 'fit-content',
          gap: '2.4rem',
        }}
      >
        <Flex
          data-testid="current-plan-container"
          sx={{
            flexDirection: 'column',
            gap: '1.6rem',
          }}
        >
          <Text variant="size21Weight600Line138" sx={{ color: 'aliceBlue' }}>
            Current Plan
          </Text>
          <Text
            variant="size32Weight700Line138"
            sx={{
              color: currentProductRef.current?.name === ProductType.SOME ? 'banana' : 'berryBlue',
            }}
          >
            {currentProductRef.current?.name}
          </Text>
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              {isOtherThanMonthlyOrYearlyDuration
                ? `${priceDuration} ${
                    priceDuration % CommonPriceInterval.YEARLY === 0 ? 'Years' : 'Months'
                  }`
                : currentProductRef.current?.price?.duration === CommonPriceInterval.YEARLY
                ? 'Yearly'
                : 'Monthly'}{' '}
              Plan
              {currentProductRef.current?.price?.duration === CommonPriceInterval.YEARLY &&
                ' (Billed Annually)'}
            </Text>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              {getFormattedPrice(selectedCurrency, currentPrice?.price)}
            </Text>
          </Flex>
        </Flex>
        <Divider sx={{ color: 'blueYonder50', m: 0 }} />
        <Flex
          data-testid="new-plan-container"
          sx={{
            flexDirection: 'column',
            gap: '1.6rem',
          }}
        >
          <Text variant="size21Weight600Line138" sx={{ color: 'aliceBlue' }}>
            New Plan
          </Text>
          <Text
            variant="size32Weight700Line138"
            sx={{
              color: selectedProduct?.name === ProductType.SOME ? 'banana' : 'berryBlue',
            }}
          >
            {selectedProduct?.name}
          </Text>
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              {isOtherThanMonthlyOrYearlyDuration
                ? `${priceDuration} ${
                    priceDuration % CommonPriceInterval.YEARLY === 0 ? 'Years' : 'Months'
                  }`
                : priceInterval === CommonPriceInterval.YEARLY
                ? 'Yearly'
                : 'Monthly'}{' '}
              Plan
              {priceInterval === CommonPriceInterval.YEARLY && ' (Billed Annually)'}
            </Text>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              {getFormattedPrice(selectedCurrency, selectedPrice?.price)}
            </Text>
          </Flex>
        </Flex>
        <Divider sx={{ color: 'blueYonder50', m: 0 }} />
        <Flex sx={{ flexDirection: 'column' }}>
          <Flex sx={{ justifyContent: 'space-between', pb: '1.6rem' }}>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              Billing Date
            </Text>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              Every{' '}
              {priceInterval === CommonPriceInterval.MONTHLY
                ? `month on the ${format(new Date(), 'do')}`
                : `year on ${format(new Date(), 'MMMM do')}`}
            </Text>
          </Flex>
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Text variant="size15Weight400" sx={{ color: 'aliceBlue70' }}>
              Credit
            </Text>
            <Text
              data-testid="credit-available"
              variant="size15Weight400"
              sx={{ color: 'aliceBlue70' }}
            >
              {creditBalance}
            </Text>
          </Flex>
          <Divider sx={{ color: 'blueYonder50', my: '2.4rem' }} />
          <Flex
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Text variant="size21Weight600Line138" sx={{ color: 'aliceBlue' }}>
              Total
            </Text>
            <Text variant="size21Weight400Line138" data-testid="total" sx={{ color: 'aliceBlue' }}>
              {getFormattedPrice(selectedCurrency, Math.max(0, Math.round(total)))}
            </Text>
          </Flex>
        </Flex>
        {transactionStatus === TransactionStatus.INPROGRESS && <RenderProcessing />}
        {errorMessage && (
          <Box sx={{ textAlign: 'center' }}>
            <Text sx={{ fontSize: '1.4rem', color: 'pomegranate' }}>{errorMessage}</Text>
          </Box>
        )}
        <Flex>
          <Button
            disabled={isButtonDisabled}
            data-testid="save-change-plan"
            ariaLabel="save plan"
            variant="gradientButton"
            sx={{
              minWidth: '100%',
            }}
            onClick={e => {
              e.stopPropagation()
              const price = Math.max(0, Math.round(total))

              if (!price) {
                handleSave()
                return
              }

              const formattedPrice = getFormattedPrice(selectedCurrency, price)

              presentAlert(
                {
                  message: `Are you sure you want to make a payment of ${formattedPrice}?`,
                  variant: 'primary',
                  isSticky: true,
                  shouldDismissOnClickOutside: true,
                  icon: ErrorIcon,
                  buttons: [
                    {
                      onClick: () => {
                        handleSave()
                        dismissAlert(alertId)
                      },
                      text: 'Yes',
                    },
                    {
                      onClick: () => {
                        dismissAlert(alertId)
                      },
                      text: 'No',
                    },
                  ],
                },
                alertId,
              )
            }}
          >
            <Text variant="size17Weight500Line138">Save</Text>
          </Button>
        </Flex>
      </Flex>
      <Flex sx={{ width: '100%', justifyContent: 'center', mt: '2.4rem' }}>
        <Link
          data-testid="edit-plan"
          to={`/plans?step=${PlansSteps.SELECT_PLAN}`}
          style={{
            textDecoration: 'none',
          }}
        >
          <Text
            variant="size17Weight500Line138"
            sx={{
              color: 'cyan800',
              ':hover': { color: 'white' },
            }}
          >
            Edit Plan
          </Text>
        </Link>
      </Flex>
    </Flex>
  )
}

export default ChangePlanSummary
